import type { NavItemData } from '@layouts/dashboard/types';

import React from 'react';
import {
  Box,
  Truck,
  Boxes,
  Undo2,
  Wrench,
  QrCode,
  MapPin,
  Trash2,
  Barcode,
  Receipt,
  FileText,
  Container,
  ListChecks,
  LayoutGrid,
  ScanBarcode,
  PackageOpen,
  PackageCheck,
  PackageSearch,
  ClipboardCheck,
  ArrowLeftRight,
  ShoppingBasket,
} from 'lucide-react';

import { useIsMobile } from '@shared/hooks/use-media-query';
import { StatusLabel } from '@shared/components/status-label';

import { Role, useUser } from '@features/user-access';

// desktopOnly: hidden on phones (printing, shipping station workflows).
type Items = NavItemData & { role: Role; enabled?: boolean; desktopOnly?: boolean };

export type MenuSection = {
  subheader?: string;
  items: Items[];
};

type MenuItem = MenuSection[];

// const isPreprod =
//   import.meta.env.VITE_APP_API_HOST!.includes('chamalo-dev') ||
//   import.meta.env.VITE_APP_API_HOST!.includes('camalo-local');

const filterMenu = (menu: MenuItem, roles: Role[], isMobile: boolean): MenuSection[] =>
  menu
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => roles.includes(item.role) && !(isMobile && item.desktopOnly)),
    }))
    .filter((section) => section.items.length > 0);

// Sidebar of the workshop area: its own apps, plus the way back to the main menu.
export const useWorkshopMenu = () => {
  const {
    userAccess: { warehouse },
  } = useUser();
  const isMobile = useIsMobile();

  const menuItems: MenuItem = [
    {
      subheader: 'Applications',
      items: [
        {
          title: 'Rangement caisses',
          caption: 'Préparer la personnalisation',
          path: '/workshop/storage',
          icon: <Boxes />,
          deepMatch: true,
          role: Role.Workshop,
        },
        {
          title: "Plan de l'atelier",
          caption: 'Voir les caisses par emplacement',
          path: '/workshop/plan',
          icon: <LayoutGrid />,
          deepMatch: true,
          role: Role.Workshop,
        },
        {
          title: 'Sessions de personnalisation',
          caption: 'Suivre les passages sur machine',
          path: '/workshop/sessions',
          icon: <ListChecks />,
          deepMatch: true,
          role: Role.Workshop,
        },
        {
          title: 'Sortie caisses',
          caption: 'Vers le convoyeur ou le papier cadeau',
          path: '/workshop/release',
          icon: <PackageOpen />,
          deepMatch: true,
          role: Role.Workshop,
        },
      ],
    },
  ];

  return { items: filterMenu(menuItems, warehouse?.roles ?? [], isMobile) };
};

export const useMenu = () => {
  const {
    userAccess: { warehouse, shops },
  } = useUser();
  const isMobile = useIsMobile();

  const totalOrders = shops!.reduce((acc, shop) => acc + (shop.orderCount ?? 0), 0);

  const menuItems: MenuItem = [
    {
      subheader: 'Flux Sortants',
      items: [
        {
          title: 'Picking',
          caption: 'Papier à imprimer',
          desktopOnly: true,
          info:
            totalOrders > 0 ? (
              <StatusLabel color="primary" variant="filled">
                {totalOrders}
              </StatusLabel>
            ) : undefined,
          path: '/warehouse/picking',
          icon: <ShoppingBasket />,
          deepMatch: true,
          role: Role.Picking,
          enabled: true,
        },
        {
          title: 'Picking scan',
          caption: 'Sur téléphone',
          info:
            totalOrders > 0 ? (
              <StatusLabel color="primary" variant="filled">
                {totalOrders}
              </StatusLabel>
            ) : undefined,
          path: '/warehouse/picking-session',
          icon: <PackageCheck />,
          deepMatch: true,
          role: Role.PickingSession,
          enabled: true,
        },
        {
          title: 'Expédition',
          desktopOnly: true,
          path: '/warehouse/shipment',
          icon: <Truck />,
          deepMatch: true,
          role: Role.Shipping,
          enabled: true,
        },
        {
          title: 'Colis',
          desktopOnly: true,
          path: `/warehouse/packaging`,
          icon: <Box />,
          deepMatch: true,
          role: Role.Shipping,
          enabled: true,
        },
        {
          title: 'Bordereau transport',
          desktopOnly: true,
          path: `/warehouse/carrier-slip`,
          icon: <FileText />,
          deepMatch: true,
          role: Role.Barcode,
          enabled: true,
        },
      ].filter((item) => item.enabled),
    },
    {
      subheader: 'Flux entrant',
      items: [
        {
          title: 'Scan réceptions & retours',
          path: '/warehouse/incoming-parcel-scan',
          icon: <ScanBarcode />,
          deepMatch: true,
          role: Role.IncomingParcelScan,
          enabled: true,
        },
        {
          title: 'Réception marchandise',
          path: '/warehouse/reception',
          icon: <Container />,
          deepMatch: true,
          role: Role.Reception,
          enabled: true,
        },
        {
          title: 'Retour',
          path: `/warehouse/order-return`,
          icon: <Undo2 />,
          deepMatch: true,
          role: Role.ReturnProduct,
          enabled: true,
        },
      ].filter((item) => item.enabled),
    },
    {
      subheader: 'Gestion des Stocks',
      items: [
        {
          title: 'Inventaire',
          path: `/warehouse/inventory`,
          icon: <ClipboardCheck />,
          deepMatch: true,
          role: Role.Inventory,
          enabled: true,
        },
        {
          title: 'Transfert / Rangement',
          path: `/warehouse/transfer`,
          icon: <ArrowLeftRight />,
          deepMatch: true,
          role: Role.Transfer,
          enabled: true,
        },
        {
          title: 'Rebut',
          path: `/warehouse/rebut`,
          icon: <Trash2 />,
          deepMatch: true,
          role: Role.Rebut,
          enabled: true,
        },
      ].filter((item) => item.enabled),
    },
    {
      subheader: 'Atelier',
      items: [
        {
          title: "Entrer dans l'atelier",
          caption: 'Applications de l’atelier',
          path: '/workshop',
          icon: <Wrench />,
          role: Role.Workshop,
          enabled: true,
        },
      ].filter((item) => item.enabled),
    },
    {
      subheader: 'Information',
      items: [
        {
          title: 'Information commande',
          path: `/warehouse/order-information`,
          icon: <Receipt />,
          deepMatch: true,
          role: Role.Print,
          enabled: true,
        },
        {
          title: 'Information produit',
          caption: 'Stock, lot et DLUO',
          path: `/warehouse/product-information`,
          icon: <PackageSearch />,
          deepMatch: true,
          role: Role.Info,
          enabled: true,
        },
      ].filter((item) => item.enabled),
    },
    {
      subheader: 'Impression',
      items: [
        {
          title: 'Impression produit',
          desktopOnly: true,
          path: `/warehouse/print-product`,
          icon: <Barcode />,
          deepMatch: true,
          role: Role.Barcode,
          enabled: true,
        },
        {
          title: 'Impression emplacement',
          desktopOnly: true,
          path: `/warehouse/print-location`,
          icon: <MapPin />,
          deepMatch: true,
          role: Role.Barcode,
          enabled: true,
        },
        {
          title: 'Impression caisse',
          desktopOnly: true,
          path: `/warehouse/print-box`,
          icon: <QrCode />,
          deepMatch: true,
          role: Role.Barcode,
          enabled: true,
        },
      ].filter((item) => item.enabled),
    },
  ];

  return { items: filterMenu(menuItems, warehouse?.roles ?? [], isMobile) };
};
